import React, { useCallback, useState } from 'react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import {
  SearchParamsPetCounts,
  SearchParamsPetType,
  SITTER_PROFILE_EVENTSTREAM_EVENTS,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { ViewerOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';

import { SitterProfileServiceChanged, SitterProfileServiceSelectorOpened } from './analyticsEvents';
import RevampServicesCtaLoadingShimmer from './RevampServicesCtaLoadingShimmer';
import RevampServicesCtaOwnerView from './RevampServicesCtaOwnerView';
import RevampServicesCtaSelfView from './RevampServicesCtaSelfView';
import RevampServiceSelector from './RevampServiceSelector';

export type CTAProps = CTAPropsCommon & {
  isApproved: boolean;
};

export type CTAPropsCommon = {
  priceTransparencyLevel: PriceTransparencyLevel;
  locale: Locale;
  petType: SearchParamsPetType;
  petCount: SearchParamsPetCounts;
  bookingFeePercentage: string;
};

const RevampServicesCta = ({
  priceTransparencyLevel,
  locale,
  petType,
  petCount,
  bookingFeePercentage,
  isApproved,
}: CTAProps): JSX.Element => {
  const { sitterData, serviceType, setServiceType, viewMode } = useRevampProfileContext();

  const theme = useTheme();

  const hasServices = sitterData.services.length > 0;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleOpenSheet = useCallback(() => {
    setIsSheetOpen(true);
    emitAnalyticsEvent(new SitterProfileServiceSelectorOpened({ providerId: sitterData.opk }));
  }, [sitterData.opk]);

  const handleCloseSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const handleServiceSave = useCallback(
    (slug: string) => {
      if (slug !== serviceType) {
        setServiceType(slug);
        fireDataLayerEvent({
          event: SITTER_PROFILE_EVENTSTREAM_EVENTS.CHANGE_SERVICE,
          serviceType: slug,
          providerId: sitterData.opk,
        });
        emitMetric('sitter_profile_service_changed', { component: 'revamp-cta' });
        emitAnalyticsEvent(
          new SitterProfileServiceChanged({
            serviceSlug: slug,
            providerId: sitterData.opk,
            pageVariant: 'revamp',
          })
        );
      }
    },
    [serviceType, setServiceType, sitterData.opk]
  );

  return (
    <>
      <Box
        data-testid="revamp-services-cta"
        px="4x"
        py="8x"
        borderRadius="16px"
        backgroundColor={theme.background.primary}
        sx={{
          boxShadow: '0px 2px 12px 0px rgba(27, 31, 35, 0.24)',
        }}
      >
        {(() => {
          if (!hasServices) return <RevampServicesCtaLoadingShimmer />;
          if (viewMode.viewer === ViewerOptions.SELF)
            return (
              <RevampServicesCtaSelfView
                priceTransparencyLevel={priceTransparencyLevel}
                locale={locale}
                petCount={petCount}
                petType={petType}
                bookingFeePercentage={bookingFeePercentage}
                handleOpenSheet={handleOpenSheet}
                isOpen={isSheetOpen}
                isApproved={isApproved}
              />
            );
          return (
            <RevampServicesCtaOwnerView
              priceTransparencyLevel={priceTransparencyLevel}
              locale={locale}
              petCount={petCount}
              petType={petType}
              bookingFeePercentage={bookingFeePercentage}
              handleOpenSheet={handleOpenSheet}
              isOpen={isSheetOpen}
            />
          );
        })()}
      </Box>
      <RevampServiceSelector
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        services={sitterData.services}
        selectedServiceSlug={serviceType}
        onSave={handleServiceSave}
        variant="desktop"
      />
    </>
  );
};

export default RevampServicesCta;
